import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-light-svg-icons/faEllipsis';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey';
import { faPause } from '@fortawesome/pro-light-svg-icons/faPause';
import { faPlay } from '@fortawesome/pro-light-svg-icons/faPlay';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import type { MailboxRow } from '../../../music24/outreach/outreachTypes';
import { mailboxMessages as messages } from './mailboxMessages';

export interface MailboxRowActions {
  test: () => void;
  pause: () => void;
  resume: () => void;
  changePassword: () => void;
  remove: () => void;
}

interface MailboxRowMenuProps {
  mailbox: MailboxRow;
  /** The owner gets every action; a member gets only the test pitch of an own mailbox (design q2). */
  isOwner: boolean;
  disabled: boolean;
  actions: MailboxRowActions;
}

/** The more menu of a mailbox row (design outreach-v2 q2). */
const MailboxRowMenuComponent: React.FC<MailboxRowMenuProps> = ({ mailbox, isOwner, disabled, actions }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const isPaused = mailbox.health === 'paused';
  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen((open) => !open)}>
      <DropdownToggle
        color="link"
        size="sm"
        className="text-body-secondary"
        aria-label={intl.formatMessage(messages.actions, { address: mailbox.address })}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem onClick={actions.test} disabled={isPaused}>
          <FontAwesomeIcon icon={faEnvelope} fixedWidth className="me-2" />
          {intl.formatMessage(messages.sendTest)}
        </DropdownItem>
        {isOwner && (
          <>
            {isPaused ? (
              <DropdownItem onClick={actions.resume}>
                <FontAwesomeIcon icon={faPlay} fixedWidth className="me-2" />
                {intl.formatMessage(messages.resume)}
              </DropdownItem>
            ) : (
              <DropdownItem onClick={actions.pause} disabled={mailbox.health === 'provisioning'}>
                <FontAwesomeIcon icon={faPause} fixedWidth className="me-2" />
                {intl.formatMessage(messages.pause)}
              </DropdownItem>
            )}
            <DropdownItem onClick={actions.changePassword}>
              <FontAwesomeIcon icon={faKey} fixedWidth className="me-2" />
              {intl.formatMessage(messages.changePassword)}
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem className="text-danger" onClick={actions.remove}>
              <FontAwesomeIcon icon={faXmark} fixedWidth className="me-2" />
              {intl.formatMessage(messages.remove)}
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

export const MailboxRowMenu = TypedMemo(MailboxRowMenuComponent);
